import { ReactElement, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import api from '@/services/api'

import matter from 'gray-matter'
import renderToString from 'next-mdx-remote/render-to-string'
import Markdown, { DEFAULT_OPTIONS as mdxOptions } from '@/components/Markdown'
import { MdxRemote } from 'next-mdx-remote/types'
import repoSlug from '@/lib/repoSlug'

import { NewRepo } from '@/@types/@repo'

import { useGlobal } from '@/context/global'
import useResize from '@/hooks/useResize'

import { animated, useSpring } from 'react-spring'

import Arrow from '@/assets/icons/arrow.svg'
import { FaBalanceScale } from 'react-icons/fa'
import {
  FiArrowLeft,
  FiStar,
  FiGitCommit,
  FiExternalLink
} from 'react-icons/fi'
import { BiGitRepoForked } from 'react-icons/bi'
import SEO from '@/components/SEO'
import Text from '@/components/Text'
import { resolveProjectSeoImage } from '@/lib/resolveProjectSeoImage'

import { Container, Main } from '@/styles/pages/Project'
import { PROJECTS_LIST_ROUTE } from '@/lib/projectRoute'

type Props = NewRepo

export default function Project(props: Props): ReactElement {
  const { NavBar } = useGlobal()
  const contentRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const maxWidth = useResize(contentRef)

  const [infoIsOpen, setInfoIsOpen] = useState(false)
  const [isMobileViewport, setIsMobileViewport] = useState(false)

  useEffect(() => {
    const syncViewport = () => {
      setIsMobileViewport(window.innerWidth <= 768)
    }

    syncViewport()
    window.addEventListener('resize', syncViewport)

    return () => {
      window.removeEventListener('resize', syncViewport)
    }
  }, [])

  useEffect(() => {
    if (!infoIsOpen) return

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node

      if (panelRef.current?.contains(target)) return
      setInfoIsOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [infoIsOpen])

  const styles = useSpring({
    transform: infoIsOpen
      ? `translate(${isMobileViewport ? 8 : 0}%, -50%)`
      : 'translate(76%, -50%)'
  })

  const createdAt = props.created_at ? new Date(props.created_at) : null
  const date =
    createdAt && !Number.isNaN(createdAt.valueOf())
      ? Intl.DateTimeFormat('pt-BR', { dateStyle: 'full' }).format(createdAt)
      : 'Data indisponível'

  useEffect(() => {
    NavBar.set(false)
    // const img = document.querySelector('.logo_symbol') as HTMLImageElement
    // img.style.setProperty('--offsetWidth', `${img.width}px`)
    return () => NavBar.set(true)
  }, [])

  return (
    <Container>
      <SEO
        title={`Carlos Miguel: ${props.name}`}
        description={`Projeto criado por Carlos Miguel em ${date}. ${props.description}`}
        image={resolveProjectSeoImage(props)}
      />
      {/* <img className="logo_symbol" src={props.logo_symbol_url} /> */}

      <animated.div ref={panelRef} className="project-data" style={styles}>
        <div
          className={`arrow ${infoIsOpen && 'open'}`}
          onClick={() => setInfoIsOpen(!infoIsOpen)}
        >
          <Arrow />
        </div>
        <div className="repo-info">
          <b title="Commits">
            <FiGitCommit />
            {props.commitsCount}
          </b>
          <b title="Curtidas">
            <FiStar />
            {props.stars}
          </b>
          <b title="Replicas">
            <BiGitRepoForked />
            {props.forks}
          </b>
          <b title="Licença">
            <FaBalanceScale />
            {props.license}
          </b>
        </div>
        <div className="hidden">
          <div className="description">
            <Text size={2} weight={700}>
              {props.name}
            </Text>
            {props.description}
          </div>
          {props.topics?.length > 0 && (
            <div className="topics">
              <Text size={1} weight={700}>
                Tópicos
              </Text>

              <ul>
                {props.topics.map((topic, index) => (
                  <li key={topic + index}>{topic}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </animated.div>

      <header>
        <Link href={PROJECTS_LIST_ROUTE} className="back-link">
          <FiArrowLeft />
          <Text size={1.2} weight={600}>
            Voltar
          </Text>
        </Link>

        <div className="container">
          <h1>{props.name}</h1>
          <div className="header-meta">
            <div className="author">
              <div className="avatar">
                <img
                  src="https://avatars.githubusercontent.com/u/57706806?v=4"
                  alt="Avatar do Carlos Miguel"
                />
              </div>
              <div className="data">
                <p className="name">Carlos Miguel</p>
                <p className="date">{date}</p>
              </div>
            </div>
            {props.links?.length > 0 && (
              <div className="header-links">
                <div className="links-list">
                  {props.links.map(link => (
                    <a
                      key={`${link.label}-${link.url}`}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span>{link.label}</span>
                      <FiExternalLink />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      <Main>
        <div className="infos">
          <div>
            <div className="description">
              <Text size={2} weight={700}>
                {props.name}
              </Text>
              {props.description}
            </div>
            <div className="info">
              <b>
                <FiGitCommit />
                {props.commitsCount}
              </b>
              <b>
                <FiStar />
                {props.stars}
              </b>
              <b>
                <BiGitRepoForked />
                {props.forks}
              </b>
              <b>
                <FaBalanceScale />
                {props.license}
              </b>
            </div>
            {props.languages && Object.keys(props.languages).length > 0 && (
              <div className="languages">
                <Text size={1} weight={700}>
                  Linguagens
                </Text>

                <ul>
                  {Object.keys(props.languages).map((language, index) => (
                    <li key={language + index}>
                      {language}: {props.languages[language]}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {props.topics?.length > 0 && (
              <div className="topics">
                <Text size={1} weight={700}>
                  Tópicos
                </Text>

                <ul>
                  {props.topics.map((topic, index) => (
                    <li key={topic + index}>{topic}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div ref={contentRef} className="content">
          <Markdown
            maxWidth={maxWidth}
            markdown={props.readme as MdxRemote.Source}
            baseUrl={props.readme_base_url}
            rootUrl={props.readme_root_url}
            projectSlug={props.slug || repoSlug(props.name)}
            currentReadmePath={props.readme_path}
          />
        </div>
      </Main>
    </Container>
  )
}

export async function getStaticPaths() {
  return {
    // Project details are generated on first access and then served by ISR.
    // This avoids rebuilding every repository whenever the portfolio deploys.
    paths: [],
    fallback: 'blocking'
  }
}

export async function getStaticProps(context) {
  const id = encodeURIComponent(String(context.params.id || ''))

  try {
    const { data: repo } = await api.get<NewRepo>(`/repos/${id}/root`)

    let { content, data } = matter(repo.readme)
    content = content.split('</img>').join('')

    const regex = /(?<=<img|<br)(.*?)(?=>)>/gi
    content.match(regex)?.map(match => {
      const changed = match.replace(' >', ' />').replace('">', '" />')
      content = content.replace(match, changed)
    })

    const mdxSource = await renderToString(content, {
      scope: data,
      mdxOptions
      // components: markdownComponents
    })

    return {
      revalidate: 86400,
      props: { ...repo, readme: mdxSource }
    }
  } catch (error) {
    console.error(`Error fetching repo ${id}:`, error.message)
    return {
      notFound: true
    }
  }
}
